import { formatMoney } from './money';
import type { ForecastResult, Money } from './types';

export interface FinancialInsight {
  code: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  finding: string;
  evidence: string;
  possibleAction: string;
}

export function generateStabilityInsights(
  forecast: ForecastResult,
  safetyBuffer: Money,
): FinancialInsight[] {
  const insights: FinancialInsight[] = [];
  if (forecast.minimumAccessibleBalance < 0) {
    insights.push({
      code: 'NEGATIVE_LIQUIDITY',
      severity: 'CRITICAL',
      finding: 'The current plan contains a projected cash shortfall.',
      evidence: `Accessible cash reaches ${formatMoney(forecast.minimumAccessibleBalance)} on ${forecast.minimumDate}.`,
      possibleAction:
        'Move an eligible commitment, add confirmed income, or preserve more prior-month cash before that date.',
    });
  } else if (forecast.minimumAccessibleBalance < safetyBuffer) {
    insights.push({
      code: 'BUFFER_BREACH',
      severity: 'WARNING',
      finding: 'Projected cash falls below your configured safety buffer.',
      evidence: `The low point is ${formatMoney(forecast.minimumAccessibleBalance)} versus a ${formatMoney(safetyBuffer)} buffer.`,
      possibleAction:
        'Delay a discretionary payment or maintain additional accessible cash before the low point.',
    });
  }
  if (forecast.accountMinimumBreach) {
    insights.push({
      code: 'ACCOUNT_MINIMUM',
      severity: 'WARNING',
      finding: 'An account is projected to fall below its minimum balance.',
      evidence: 'At least one scheduled event crosses its account-specific threshold.',
      possibleAction:
        'Choose another eligible funding account or transfer funds before the obligation is due.',
    });
  }
  return insights;
}
